/* This example requires Tailwind CSS v2.0+ */
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Disclosure, Menu, Transition } from '@headlessui/react'
import { BellIcon, MenuIcon, XIcon } from '@heroicons/react/outline'
import {
  ChevronDownIcon,
  UserIcon,
  OfficeBuildingIcon,
  LogoutIcon,
} from '@heroicons/react/outline'
import Link from 'next/link'
import { NAV_DEF } from './consts'
import { useChanged } from '../../hooks/useChanged'
import { MobileNav } from './MobileNav'
import { useEmployeeAuth } from '../../hooks/useEmployeeAuth'
import FontDownloadOutlinedIcon from '@mui/icons-material/FontDownloadOutlined'
import Tooltip from '@mui/material/Tooltip'

const NAV_DEF_ARR = [...NAV_DEF.values()]
function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

function getInitials(name) {
  if (!name) return '?'
  return name
    .split(' ')
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const Navbar = () => {
  const [subNavIndex, setSubNavIndex] = useState()
  const [navHeight, setNavHeight] = useState(0)

  const {
    isAuthenticated: isEmployeeAuthenticated,
    account: employeeAccount,
    login: employeeLogin,
    logout: employeeLogout,
  } = useEmployeeAuth()

  const elRef = useRef([])

  const animate = useCallback((newValue, oldValue) => {
    if (newValue !== undefined && elRef.current[newValue]) {
      elRef.current[newValue].scrollTop = 0
    }

    if (oldValue === undefined) {
      const newEl = elRef.current[newValue]
      const cl = newEl.classList

      cl.remove('md:-translate-x-full', 'md:transition-all')
      cl.add('md:translate-x-full')
      cl.add('md:transition-all')
      cl.remove('md:translate-x-full', 'md:opacity-0')
      setNavHeight(newEl.clientHeight)
    } else if (newValue === undefined) {
      const oldEl = elRef.current[oldValue]
      oldEl?.classList.remove('md:translate-x-0')
      oldEl?.classList.add(
        'md:translate-x-full',
        'md:transition-all',
        'md:opacity-0'
      )
      setNavHeight(0)
    } else {
      const newEl = elRef.current[newValue]
      const oldEl = elRef.current[oldValue]
      const ncl = newEl.classList
      const ocl = oldEl.classList
      ocl.remove(
        'md:-translate-x-full',
        'md:translate-x-full',
        'md:transition-all'
      )

      if (oldValue > newValue) {
        ncl.remove('md:translate-x-full', 'md:transition-all')
        ncl.add('md:-translate-x-full')

        ocl.add('md:translate-x-full', 'md:transition-all', 'md:opacity-0')
        ncl.add('md:transition-all')
        ncl.remove('md:-translate-x-full', 'md:opacity-0')
        setNavHeight(newEl.clientHeight)
      } else {
        ncl.remove('md:-translate-x-full', 'md:transition-all')
        ncl.add('md:translate-x-full')

        ocl.add('md:-translate-x-full', 'md:transition-all', 'md:opacity-0')
        ncl.add('md:transition-all')
        ncl.remove('md:translate-x-full', 'md:opacity-0')
        setNavHeight(newEl.clientHeight)
      }
    }
  }, [])

  useChanged(subNavIndex, animate)

  const [readingFriendlyMode, setReadingFriendlyMode] = useState(false)

  const isBrowser = typeof window !== 'undefined'

  const isReadingFriendlyMode =
    isBrowser && window.localStorage.getItem('readingFriendlyMode') === 'true'

  useEffect(() => {
    if (isReadingFriendlyMode && isBrowser) {
      document.body.classList.add('reading-friendly-mode')
    }
    setReadingFriendlyMode(isReadingFriendlyMode)
  }, [])

  function applyReadingStyles() {
    document.body.style.fontFamily = "'Open Dyslexic', system-ui, sans-serif"
    document.body.style.letterSpacing = '0.35ch'
    document.body.style.wordSpacing = '1.225ch'
    document.body.style.fontVariantLigatures = 'none'
    document.body.style.fontWeight = '600'
    document.body.style.fontSize = '105%'
  }

  function removeReadingStyles() {
    document.body.style.fontFamily = ''
    document.body.style.letterSpacing = ''
    document.body.style.wordSpacing = ''
    document.body.style.fontVariantLigatures = ''
    document.body.style.fontWeight = ''
    document.body.style.fontSize = ''
  }

  const toggleReadingFriendlyMode = () => {
    if (readingFriendlyMode) {
      window.localStorage.setItem('readingFriendlyMode', false)
      removeReadingStyles()
      setReadingFriendlyMode(false)
    } else {
      window.localStorage.setItem('readingFriendlyMode', true)
      applyReadingStyles()
      setReadingFriendlyMode(true)
    }
  }

  return (
    <Disclosure
      as="nav"
      className="fixed top-0 left-0 z-50 w-full bg-blue-dark h-92px"
      onMouseLeave={() => setSubNavIndex()}
    >
      {({ open }) => (
        <>
          <div className="mx-auto px-2 sm:px-6 lg:px-16 font-[sans-serif]">
            <div className="relative flex items-center justify-between h-24">
              <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                {/* Mobile menu button*/}
                <Disclosure.Button className="inline-flex items-center justify-center p-2 text-white rounded-md hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XIcon className="block w-6 h-6" aria-hidden="true" />
                  ) : (
                    <MenuIcon className="block w-6 h-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
              <div className="flex items-center justify-center flex-1 sm:items-stretch sm:justify-start">
                <div className="flex items-center flex-shrink-0">
                  <Link href="/">
                    <img
                      className="hidden w-auto h-8 cursor-pointer xl:block"
                      src="EvryHealth_Logo_RGB_COLOR_WHITE_TEXT.svg"
                      alt="Evry Health Logo"
                    />
                  </Link>
                </div>
                <div className="hidden sm:block sm:ml-6">
                  <div className="flex space-x-4">
                    {[...NAV_DEF.values()].map((item, i) => (
                      <button
                        key={item.name}
                        onMouseOver={() => setSubNavIndex(i)}
                        className={
                          'text-white hover:text-white-light px-3 py-2 rounded-md cursor-pointer uppercase'
                        }
                        aria-current={item.current ? 'page' : undefined}
                      >
                        {item.name}
                      </button>
                    ))}
                    <Link href="/contact-us">
                      <button className="px-3 py-2 text-white uppercase rounded-md cursor-pointer hover:text-white-light">
                        Contact
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                <div className="hidden min-[1600px]:flex flex-row gap-6">
                  <a
                    href="tel:1-855-579-3879"
                    className="cursor-pointer text-blue-light hover:text-white-light hover:underline"
                  >
                    1-855-579-3879
                  </a>
                  <a
                    href="mailto:support@evryhealth.com"
                    className="pr-4 text-blue-light hover:text-white-light hover:underline"
                  >
                    support@evryhealth.com
                  </a>
                </div>
                {/* Overflow menu shows the contact details hidden on smaller screens */}
                <div className="relative hidden group sm:block min-[1600px]:hidden">
                  <button
                    type="button"
                    aria-label="More contact options"
                    aria-haspopup="true"
                    className="px-3 py-2 text-2xl leading-none text-white rounded-md cursor-pointer hover:text-white-light"
                  >
                    &hellip;
                  </button>
                  <div className="absolute right-0 z-50 hidden pt-2 group-hover:block group-focus-within:block">
                    <div className="flex flex-col gap-2 p-4 rounded-md shadow-lg bg-blue-dark whitespace-nowrap">
                      <a
                        href="tel:1-855-579-3879"
                        className="text-blue-light hover:text-white-light hover:underline"
                      >
                        1-855-579-3879
                      </a>
                      <a
                        href="mailto:support@evryhealth.com"
                        className="text-blue-light hover:text-white-light hover:underline"
                      >
                        support@evryhealth.com
                      </a>
                    </div>
                  </div>
                </div>
                <Menu as="div" className="relative ml-3">
                  {({ open }) =>
                    isEmployeeAuthenticated ? (
                    <>
                      <Menu.Button
                        type="button"
                        className="flex items-center gap-2 py-2 pl-2 pr-3 text-sm text-white transition-colors rounded-none shadow-sm bg-blue hover:bg-blue-light group"
                      >
                        <span className="flex items-center justify-center w-8 h-8 text-xs font-semibold rounded-full bg-white text-blue">
                          {getInitials(employeeAccount?.name)}
                        </span>
                        <span className="hidden max-w-[10rem] truncate md:inline">
                          {employeeAccount?.name || 'Account'}
                        </span>
                        <ChevronDownIcon
                          className={classNames(
                            'w-4 h-4 transition-transform',
                            open ? 'rotate-180' : ''
                          )}
                          aria-hidden="true"
                        />
                      </Menu.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-150"
                        enterFrom="transform opacity-0 scale-95 -translate-y-1"
                        enterTo="transform opacity-100 scale-100 translate-y-0"
                        leave="transition ease-in duration-100"
                        leaveFrom="transform opacity-100 scale-100 translate-y-0"
                        leaveTo="transform opacity-0 scale-95 -translate-y-1"
                      >
                        <Menu.Items className="absolute right-0 z-50 mt-2 overflow-hidden origin-top-right bg-white shadow-xl w-72 rounded-none ring-1 ring-black/5 focus:outline-none">
                          <div className="flex items-center gap-3 px-4 py-4 bg-gradient-to-br from-blue-dark to-blue">
                            <span className="flex items-center justify-center flex-shrink-0 text-sm font-semibold text-white rounded-full h-11 w-11 bg-white/20">
                              {getInitials(employeeAccount?.name)}
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-white truncate">
                                {employeeAccount?.name || 'Employee'}
                              </p>
                              <p className="text-xs truncate text-blue-light">
                                {employeeAccount?.username}
                              </p>
                            </div>
                          </div>
                          <div className="py-1">
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  type="button"
                                  onClick={employeeLogout}
                                  className={classNames(
                                    active ? 'bg-gray-50 text-blue' : 'text-blue-dark',
                                    'flex w-full items-center gap-3 px-4 py-3 text-sm transition-colors'
                                  )}
                                >
                                  <LogoutIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
                                  Sign Out
                                </button>
                              )}
                            </Menu.Item>
                          </div>
                        </Menu.Items>
                      </Transition>
                    </>
                  ) : (
                    <>
                      <Menu.Button
                        type="button"
                        className="flex items-center gap-2 px-8 py-2 text-sm text-white transition-colors rounded-none shadow-sm bg-blue hover:bg-white-light hover:text-blue"
                      >
                        Login
                        <ChevronDownIcon
                          className={classNames(
                            'w-4 h-4 transition-transform',
                            open ? 'rotate-180' : ''
                          )}
                          aria-hidden="true"
                        />
                      </Menu.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-150"
                        enterFrom="transform opacity-0 scale-95 -translate-y-1"
                        enterTo="transform opacity-100 scale-100 translate-y-0"
                        leave="transition ease-in duration-100"
                        leaveFrom="transform opacity-100 scale-100 translate-y-0"
                        leaveTo="transform opacity-0 scale-95 -translate-y-1"
                      >
                        <Menu.Items className="absolute right-0 z-50 mt-2 overflow-hidden origin-top-right bg-white shadow-xl w-72 rounded-none ring-1 ring-black/5 focus:outline-none">
                          <div className="py-1">
                            <Menu.Item>
                              {({ active }) => (
                                <a
                                  href="https://portal.evryhealth.com/sign-in"
                                  target="_blank"
                                  rel="noreferrer"
                                  className={classNames(
                                    active ? 'bg-gray-50' : '',
                                    'flex items-start gap-3 px-4 py-3 transition-colors'
                                  )}
                                >
                                  <UserIcon className="w-5 h-5 mt-0.5 flex-shrink-0 text-blue" aria-hidden="true" />
                                  <span>
                                    <span className="block text-sm font-semibold text-blue-dark">
                                      Member Login
                                    </span>
                                    <span className="block text-xs text-gray-500">
                                      Access your member portal
                                    </span>
                                  </span>
                                </a>
                              )}
                            </Menu.Item>
                            <div className="mx-4 border-t border-gray-100" />
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  type="button"
                                  onClick={employeeLogin}
                                  className={classNames(
                                    active ? 'bg-gray-50' : '',
                                    'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors'
                                  )}
                                >
                                  <OfficeBuildingIcon className="w-5 h-5 mt-0.5 flex-shrink-0 text-blue" aria-hidden="true" />
                                  <span>
                                    <span className="block text-sm font-semibold text-blue-dark">
                                      Employee Login
                                    </span>
                                    <span className="block text-xs text-gray-500">
                                      Sign in with Microsoft
                                    </span>
                                  </span>
                                </button>
                              )}
                            </Menu.Item>
                          </div>
                        </Menu.Items>
                      </Transition>
                    </>
                    )
                  }
                </Menu>
                <Tooltip
                  title={
                    <span style={{ fontSize: '16px' }}>
                      Reading friendly mode
                    </span>
                  }
                >
                  <Menu as="div" className="relative ml-3">
                    <button
                      onClick={toggleReadingFriendlyMode}
                      type="button"
                      className={`px-3 py-2 text-sm text-white rounded-none shadow-s ${
                        readingFriendlyMode
                          ? 'bg-white-light hover:bg-blue !text-blue hover:!text-white-light'
                          : 'bg-blue hover:bg-white-light hover:text-blue'
                      } `}
                    >
                      <FontDownloadOutlinedIcon />
                    </button>
                  </Menu>
                </Tooltip>
              </div>
            </div>
          </div>
          <div
            className="hidden md:overflow-clip md:block md:relative md:transition-all"
            style={{ height: `${navHeight}px` }}
          >
            {[...NAV_DEF.values()].map((subnav, i) => (
              <div
                key={subnav.name}
                ref={el => (elRef.current[i] = el)}
                className="grid grid-cols-2 text-white md:p-16 md:w-full md:absolute md:translate-x-full md:max-h-[calc(100vh-6rem)] md:overflow-x-hidden md:overflow-y-auto md:overscroll-contain"
              >
                <div className="col-span-2 px-4 md:col-span-1 md:px-16">
                  <h2 className="text-xl font-bold text-blue-light font-[sans-serif] pb-6">
                    {subnav.name}
                  </h2>
                  <div className="">
                    {subnav?.sections
                      .filter(
                        item => !item.employeeOnly || isEmployeeAuthenticated
                      )
                      .map(item => (
                      <div className="flex py-4" key={item.name}>
                        <object
                          className="w-auto h-4 mt-2 ml-2"
                          type="image/svg+xml"
                          data={item.icon}
                        ></object>
                        <div className="inline-block px-8">
                          {item.target === '_blank' ? (
                            <a href={item.link} target={item.target}>
                              <div className="inline-block">
                                <h3 className="inline-block text-xl  hover:underline hover:text-white-light text-blue-light font-[sans-serif] cursor-pointer">
                                  {item.name}
                                </h3>
                              </div>
                            </a>
                          ) : (
                            <Link href={item.link} target={item.target}>
                              <div
                                onClick={() => setSubNavIndex()}
                                className="inline-block"
                              >
                                <h3 className="inline-block text-xl  hover:underline hover:text-white-light text-blue-light font-[sans-serif] cursor-pointer">
                                  {item.name}
                                </h3>
                              </div>
                            </Link>
                          )}

                          <p>{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-span-2 px-4 md:col-span-1 md:px-16">
                  <h2 className="text-xl font-bold text-blue-light font-[sans-serif] uppercase">
                    Related Resource
                  </h2>
                  <img
                    className="mt-6"
                    src={subnav.related.media}
                    alt={subnav.related.media}
                  />
                  <div className="flex py-4 mt-10 max-w-475">
                    <div className="inline-block pr-6">
                      <Link href="#" className="inline-block">
                        <h3 className="inline-block text-xl text-blue-light font-[sans-serif]">
                          {subnav.related.title}
                        </h3>
                      </Link>
                      <p>{subnav.related.description}</p>
                    </div>
                    <div>
                      <Link href={subnav.related.link}>
                        <button className="inline-block py-2 mt-8 text-sm text-center bg-transparent border-2 w-150 text-blue-light hover:bg-blue-light hover:text-white-light border-blue-light">
                          {subnav.related.button}
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <MobileNav open={open} />
        </>
      )}
    </Disclosure>
  )
}

export default Navbar
